import type { ReactNode } from 'react';
import { languages, useI18n } from '../i18n';
import { guideContent, BLUESTACKS_DOWNLOAD_URL, type GuideBlock } from '../guide/content';

// Página pública /guia — RENDERER del contenido único de src/guide/content.ts
// (misma fuente para la web y para los PDF del ZIP, que se imprimen desde esta
// página con ?lang=xx; ver packaging/guide/export_pdfs.ps1). Para cambiar el
// texto de la guía NO se toca este fichero: se edita content.ts.

// Marcado inline soportado: **negrita**, `código`, [texto](url).
const INLINE_TOKEN = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

// Cada mención de "BlueStacks" / "bluestacks.com" en texto plano o negrita se
// convierte en enlace de descarga (BLUESTACKS_DOWNLOAD_URL). No se toca en código
// (`…`) ni en enlaces ya escritos, ni en títulos/etiquetas (no pasan por aquí).
function linkBluestacks(text: string, keyBase: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const match of text.matchAll(/bluestacks(?:\.com)?/gi)) {
    const start = match.index ?? 0;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(
      <a key={`${keyBase}-bs${n}`} href={BLUESTACKS_DOWNLOAD_URL} target="_blank" rel="noopener">
        {match[0]}
      </a>,
    );
    last = start + match[0].length;
    n += 1;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function renderInline(text: string): ReactNode[] {
  return text.split(INLINE_TOKEN).map((token, index) => {
    if (token.startsWith('**') && token.endsWith('**')) {
      return <strong key={index}>{linkBluestacks(token.slice(2, -2), String(index))}</strong>;
    }
    if (token.startsWith('`') && token.endsWith('`')) {
      return <code key={index}>{token.slice(1, -1)}</code>;
    }
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
    if (link) {
      return (
        <a key={index} href={link[2]} target={link[2].startsWith('http') ? '_blank' : undefined} rel="noopener">
          {link[1]}
        </a>
      );
    }
    return <span key={index}>{linkBluestacks(token, String(index))}</span>;
  });
}

function Block({ block }: { block: GuideBlock }) {
  switch (block.kind) {
    case 'p':
      return <p>{renderInline(block.text)}</p>;
    case 'list':
      return (
        <ul>
          {block.items.map((item, index) => (
            <li key={index}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <div>
          {block.items.map((item, index) => (
            <div className="guide-step" key={index}>
              <div className="guide-step-n">{index + 1}</div>
              <div className="guide-step-body">{renderInline(item)}</div>
            </div>
          ))}
        </div>
      );
    case 'callout':
      return (
        <div className={`guide-callout guide-callout-${block.tone}`}>
          <strong className="guide-callout-label">{block.label}</strong>
          {block.text && <span>{renderInline(block.text)}</span>}
          {block.items && (
            <ul style={{ margin: '4px 0 0' }}>
              {block.items.map((item, index) => (
                <li key={index}>{renderInline(item)}</li>
              ))}
            </ul>
          )}
        </div>
      );
    case 'faq':
      return (
        <div>
          {block.items.map((item, index) => (
            <details className="guide-faq" key={index} open={PRINT_MODE || undefined}>
              <summary>{item.q}</summary>
              <p>{renderInline(item.a)}</p>
            </details>
          ))}
        </div>
      );
  }
}

// ?print=1: los FAQ se renderizan abiertos (los PDF se imprimen desde esta página).
const PRINT_MODE = new URLSearchParams(window.location.search).has('print');

// Vídeo tutorial completo (YouTube, 2026-08-07): la instalación entera desde
// cero, en inglés. Enlazado arriba de la guía en todos los idiomas — para quien
// prefiere VER el proceso antes que leerlo. En print (?print=1, los PDF) se
// omite el banner: un enlace no clicable en papel solo mete ruido.
const VIDEO_URL = 'https://youtu.be/G774G5X1P0Q';
const VIDEO_LABEL: Record<string, string> = {
  es: '▶ Mira la instalación completa en vídeo (7 min, en inglés)',
  en: '▶ Watch the full installation on video (7 min)',
  fr: '▶ Regardez l’installation complète en vidéo (7 min, en anglais)',
  de: '▶ Sieh dir die komplette Installation als Video an (7 Min., auf Englisch)',
  pt: '▶ Veja a instalação completa em vídeo (7 min, em inglês)',
  ru: '▶ Посмотрите полную установку на видео (7 мин, на английском)',
  ko: '▶ 전체 설치 과정을 영상으로 보기 (7분, 영어)',
  zh: '▶ 觀看完整安裝影片（7 分鐘，英文）',
  ja: '▶ インストール手順を動画で見る（7分・英語）',
  tr: '▶ Kurulumun tamamını videoda izleyin (7 dk, İngilizce)',
};

// Capturas REALES del tutorial (rodaje 2026-08-06), por sección. Las mismas para
// todos los idiomas — la imagen no necesita traducción y así content.ts y su
// test de paridad no se tocan. Datos personales difuminados EN ORIGEN (las
// imágenes salen del montaje del vídeo, ya censurado). Viven en public/guide/.
const SECTION_SHOTS: Record<string, string[]> = {
  download: ['/guide/panel-download.jpg'],
  install: ['/guide/smartscreen.jpg', '/guide/installer-language.jpg'],
  bluestacks: ['/guide/bluestacks-web.jpg', '/guide/play-signin.jpg', '/guide/link-account.jpg'],
  firstrun: ['/guide/console-email.jpg', '/guide/access-code.jpg'],
  usage: ['/guide/panel-connected.jpg', '/guide/bot-running.jpg'],
};

export function GuidePage() {
  const { language, setLanguage } = useI18n();
  const content = guideContent[language];

  return (
    <div className="guide-page">
      <nav className="guide-nav">
        <a href="/" className="guide-nav-logo">WB</a>
        <div className="guide-nav-right">
          <select value={language} onChange={(event) => setLanguage(event.target.value as typeof language)}>
            {languages.map((item) => (
              <option key={item.code} value={item.code}>{item.label}</option>
            ))}
          </select>
          <a href="/" className="guide-nav-back">{language === 'es' ? '← Volver' : '← Back'}</a>
        </div>
      </nav>
      <div className="guide-content">
        <h1>{content.title}</h1>
        <p className="guide-lead">{renderInline(content.lead)}</p>
        {!PRINT_MODE && (
          <a className="guide-video" href={VIDEO_URL} target="_blank" rel="noopener">
            {VIDEO_LABEL[language] ?? VIDEO_LABEL.en}
          </a>
        )}
        <p className="guide-updated">v0.3.0 · {content.updated}</p>

        <div className="guide-toc">
          <strong>{content.tocTitle}</strong>
          <ol>
            {content.sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ol>
        </div>

        {content.sections.map((section, index) => (
          <section key={section.id} id={section.id} className="guide-section">
            <h2>
              {index + 1}. {section.title}
            </h2>
            {section.blocks.map((block, blockIndex) => (
              <Block key={blockIndex} block={block} />
            ))}
            {SECTION_SHOTS[section.id]?.map((src) => (
              <img key={src} className="guide-shot" src={src} alt={section.title} loading="lazy" />
            ))}
          </section>
        ))}

        <footer className="guide-footer">
          White Bot · whitebot.app · {content.updated}
        </footer>
      </div>
    </div>
  );
}
